import type { Artwork } from "@design-lab/shared/types/artwork";
import type * as models from "common/utils/models";

// Type for DTF artwork that may have dimensions from various sources
export type DtfArtworkWithDimensions =
  | models.cable.DtfChannel.fetchDeliveryDate.Artwork
  | models.dtf.getArtworkHistoryDetails.DetailedArtwork
  | (Artwork & {
      width?: number;
      height?: number;
      originalWidth?: number;
      originalHeight?: number;
      original?: {
        width?: number;
        height?: number;
      };
      ordered?: {
        width?: number;
        height?: number;
      };
    });

/**
 * Extracts dimensions from artwork data, checking multiple possible locations
 * @param artwork The artwork object from various API responses
 * @returns Object with width and height, or undefined if not found
 */
export function getArtworkDimensions(
  artwork: DtfArtworkWithDimensions | undefined | null
): { width: number; height: number } | undefined {
  if (!artwork) {
    return undefined;
  }

  // Check for dimensions at root level (common in frontend state)
  if ("width" in artwork && "height" in artwork && artwork.width && artwork.height) {
    return {
      width: artwork.width,
      height: artwork.height
    };
  }

  // Check for originalWidth/originalHeight (alternative naming)
  if (
    "originalWidth" in artwork &&
    "originalHeight" in artwork &&
    artwork.originalWidth &&
    artwork.originalHeight
  ) {
    return {
      width: artwork.originalWidth,
      height: artwork.originalHeight
    };
  }

  // Check in original section (from getArtworkHistoryDetails)
  if (
    "original" in artwork &&
    artwork.original &&
    artwork.original.width &&
    artwork.original.height
  ) {
    return {
      width: artwork.original.width,
      height: artwork.original.height
    };
  }

  // Check in ordered section (from getArtworkHistoryDetails)
  if ("ordered" in artwork && artwork.ordered && artwork.ordered.width && artwork.ordered.height) {
    return {
      width: artwork.ordered.width,
      height: artwork.ordered.height
    };
  }

  // Check in metadata sections (from fetchDeliveryDate)
  if (
    "originalMetadata" in artwork &&
    artwork.originalMetadata &&
    artwork.originalMetadata.width &&
    artwork.originalMetadata.height
  ) {
    return {
      width: artwork.originalMetadata.width,
      height: artwork.originalMetadata.height
    };
  }

  if (
    "metadata" in artwork &&
    artwork.metadata &&
    artwork.metadata.width &&
    artwork.metadata.height
  ) {
    return {
      width: artwork.metadata.width,
      height: artwork.metadata.height
    };
  }

  return undefined;
}

/**
 * Calculates aspect ratio from artwork data
 * @param artwork The artwork object from various API responses
 * @returns The aspect ratio (width/height) or undefined if dimensions not found
 */
export function getArtworkAspectRatio(
  artwork: DtfArtworkWithDimensions | undefined | null
): number | undefined {
  const dimensions = getArtworkDimensions(artwork);

  if (!dimensions) {
    return undefined;
  }

  return dimensions.width / dimensions.height;
}
